import {
  Page,
  Tabs,
  Card,
  ResourceList,
  Thumbnail,
  Text,
  Badge,
  BlockStack,
  InlineStack,
  Divider,
  Bleed,
  EmptyState,
} from "@medentic/polaris-react";
import { useState, useCallback } from "react";
import { SettingsPageWrapper } from "@shared/ui/Settings";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { EntityType } from "@shared/types/commonTypes";

interface Integration {
  id: string;
  name: string;
  icon: string;
  isInstalled: boolean;
}

const PREDEFINED_INTEGRATIONS: Integration[] = [
  {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    icon: '/images/svg/integrations/whatsapp.svg',
    isInstalled: false
  },
  {
    id: 'viber',
    name: 'Viber Business',
    icon: '/images/svg/integrations/viber.svg',
    isInstalled: false,
  },
  {
    id: 'sms',
    name: 'SMS Gateway',
    icon: '/images/svg/integrations/sms.svg',
    isInstalled: false,
  },
  {
    id: 'google-analytics',
    name: 'Google Business Profile',
    icon: '/images/svg/integrations/google-business-profile.svg', // Using placeholder
    isInstalled: false,
  },
  {
    id: 'gmail',
    name: 'Gmail',
    icon: '/images/svg/integrations/gmail.svg', // Using placeholder
    isInstalled: false,
  },
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    icon: '/images/svg/integrations/google-calendar.svg', // Using placeholder
    isInstalled: false,
  },
  {
    id: 'apple-calendar',
    name: 'Apple Calendar',
    icon: '/images/svg/integrations/apple-calendar.svg', // Using placeholder
    isInstalled: false,
  },
  {
    id: 'stripe',
    name: 'Stripe',
    icon: '/images/svg/integrations/stripe.svg', // Using placeholder
    isInstalled: false,
  },
  
];

export default function SettingsIntegrationsPage() {
  const { t } = useTranslation();
  const params = useParams<{ clinicId: string }>();
  const clinicId = params.clinicId ?? "";
  const [selectedTab, setSelectedTab] = useState(0);
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  }, []);

  const tabs = [
    {
      id: 'all',
      content: t("settings.integrations.tabs.all"),
      panelID: 'all-integrations',
    },
    {
      id: 'installed',
      content: t("settings.integrations.tabs.installed"),
      panelID: 'installed-integrations',
    },
  ];

  const filteredIntegrations = selectedTab === 0 
    ? PREDEFINED_INTEGRATIONS 
    : PREDEFINED_INTEGRATIONS.filter(integration => integration.isInstalled);

  const renderIntegrationItem = (integration: Integration) => {
    const { id, name, icon, isInstalled } = integration;

    return (
      <ResourceList.Item
        id={id}
        media={
          <Thumbnail 
            size="extraSmall" 
            alt={t("settings.integrations.accessibility.logoAlt", { name })}
            source={icon}
          />
        }
        accessibilityLabel={t("settings.integrations.accessibility.integrationItem", { name })}
        onClick={() => {}} // Empty onClick to satisfy the required prop
      >
        <InlineStack align="space-between" blockAlign="center">
          <Text as="span" variant="bodyMd" fontWeight="medium">
            {name}
          </Text>
          {isInstalled ? (
            <Badge tone="success">{t("settings.integrations.status.installed")}</Badge>
          ) : (
            <Badge>{t("settings.integrations.status.available")}</Badge>
          )}
        </InlineStack>
      </ResourceList.Item>
    );
  };

  return (
    <SettingsPageWrapper entityType={EntityType.CLINIC} entityId={clinicId}>
      <Page title={t("settings.integrations.title")}>
        <Card>
          <Bleed marginInline="400" marginBlock="400">
            <BlockStack gap="0">
              <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
              <Divider />
              {filteredIntegrations.length === 0 && selectedTab === 1 ? (
                <EmptyState
                  heading={t("settings.integrations.emptyState.title")}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>{t("settings.integrations.emptyState.description")}</p>
                </EmptyState>
              ) : (
                <ResourceList
                  resourceName={{
                    singular: t("settings.integrations.resource.singular"),
                    plural: t("settings.integrations.resource.plural"),
                  }}
                  items={filteredIntegrations}
                  renderItem={renderIntegrationItem}
                  showHeader={false}
                />
              )}
            </BlockStack>
          </Bleed>
        </Card>
      </Page>
    </SettingsPageWrapper>
  );
}
